/* eslint-disable @typescript-eslint/no-unused-vars */
import { DeleteOutlined, EyeOutlined, LoadingOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { uploadMedia } from "@api/uploadMediaApi";
import { Button, message, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import { SlotPatternValue } from "@page/Slot/constant";
import React, { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import PhotoModal from "./PhotoModal";

interface Props {
  imageUrl?: string;
  finishUpload: (url: string) => void;
  limitSize?: number; // in MB
  ratio?: [number, number]; // 長:寬
  mode?: "small" | "normal";
  hasPreviewAndDelete?: boolean;
  onDelete?: () => void;
  editable?: boolean;
  dimension?: [number, number]; // 圖片尺寸 [高:寬]
  limitDimension?: number; // 圖片最小尺寸 高或寬
  slotPattern?: number;
}

const UploadWrapper = styled(Upload)<{ editable?: boolean }>`
  position: relative;
  width: auto;

  &:hover {
    #upload-hover-mask {
      display: flex;
    }
  }

  #upload-hover-mask {
    display: none;
  }

  .ant-upload.ant-upload-select-picture-card {
    margin-right: 0;
    margin-bottom: 0;
    border: ${({ theme, editable }) => (editable === false ? `2px dashed ${theme.colorSecondary500}` : "")};
    &:hover {
      border: ${({ theme, editable }) => (editable === false ? `2px dashed ${theme.colorSecondary500}` : "#d9d9d9")};
    }
  }
`;
const UploadButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const UploadButtonText = styled.p`
  margin-top: 8px;
`;
const ImageWrapper = styled.img`
  max-width: 100px;
  max-height: 100%;
`;
const SmallImageWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 170px;
  height: 32px;
  padding: 6px 10px;
  border: 1px solid #d9d9d9;
`;
const SmallImage = styled.img`
  max-width: 20px;
  max-height: 100%;
  margin-right: 6px;
`;
const SmallImageTitle = styled.div`
  flex: auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const DeleteIcon = styled(DeleteOutlined)`
  margin-left: 6px;
  font-size: 12px;
`;
const HoverMask = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
`;
const MaskIconContainer = styled.div`
  display: flex;
  align-items: center;
  color: #ffffff;
  font-size: 18px;
`;
const PreviewIcon = styled(EyeOutlined)`
  margin-right: 14px;
`;

const checkSize = (file: File, size: number) => {
  if (file.size <= size * 1024 * 1024) return true;
  message.error(`大小上限為${size}MB`);
  return false;
};

const checkAspectRatio = (file: File, ratio: [number, number]) => {
  return new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.src = URL.createObjectURL(file);
    image.onload = () => {
      const { width, height } = image;

      if (Math.abs(width / height - ratio[0] / ratio[1]) < 0.1) {
        resolve();
      } else {
        message.error(`長:寬=${ratio[0]}:${ratio[1]}`);
        reject();
      }
    };
    image.onerror = reject;
  });
};

const checkAspectDimension = (
  file: File,
  dimension: [number, number],
  slotPattern?: number,
  limitDimension?: number,
) => {
  return new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.src = URL.createObjectURL(file);
    image.onload = () => {
      const { width, height } = image;

      if (height === dimension[0] && width === dimension[1]) {
        resolve();
      } else {
        message.error(`尺寸為${dimension[0]}x${dimension[1]}(單位px)`);
        reject();
      }
      // 最小寬度1280三月上線
      // if (dimension) {
      //   if (height === dimension[0] && width === dimension[1]) {
      //     resolve();
      //   } else {
      //     message.error(`尺寸為${dimension[0]}x${dimension[1]}(單位px)`);
      //     reject();
      //   }
      // }
      //
      //  else if (limitDimension) {
      //   if (
      //     (slotPattern === SlotPatternValue.CAROUSEL_PATTERN ||
      //       slotPattern === SlotPatternValue.FLAT_CAROUSEL_PATTERN) &&
      //     width < limitDimension
      //   ) {
      //     message.error(`最小寬度:${limitDimension}`);
      //     reject();
      //   } else {
      //     resolve();
      //   }
      // }
    };
    image.onerror = reject;
  });
};

const UploadImageButton: FC<Props> = (props) => {
  const {
    imageUrl,
    finishUpload,
    limitSize,
    ratio,
    mode,
    hasPreviewAndDelete,
    onDelete,
    editable = true,
    dimension,
    slotPattern,
    limitDimension,
  } = props;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [localImageUrl, setLocalImageUrl] = useState<string | undefined>(imageUrl);
  const [imageName, setImageName] = useState<string>();
  const [photoSource, setPhotoSource] = useState<string>("");

  useEffect(() => {
    if (!imageUrl?.startsWith("/efs")) {
      // efs暫時路徑不做顯示
      setLocalImageUrl(imageUrl);
    }
  }, [imageUrl]);

  async function handleUploadImage(file: RcFile) {
    setIsLoading(true);
    try {
      const result = await uploadMedia(file);
      finishUpload(result);
      setIsLoading(false);
      // 預覽上傳的圖片
      setImageName(file.name);
      setLocalImageUrl(URL.createObjectURL(file));
    } catch (error: any) {
      setIsLoading(false);
      message.error(error.mesaage);
    }

    return "";
  }

  function handleBeforeUpload(file: RcFile) {
    const validatedSize = limitSize ? checkSize(file, limitSize) : true; // 有傳limitSize才做驗證
    const validatedRatio = ratio ? checkAspectRatio(file, ratio) : true; // 有傳ratio才做驗證
    const validatedDimension = dimension ? checkAspectDimension(file, dimension) : true; // 有傳dimension才做驗證
    // const validatedDimension =
    //   limitDimension || dimension ? checkAspectDimension(file, dimension, slotPattern, limitDimension) : true; // 有傳dimension或limitDimension才做驗證

    return validatedSize && validatedRatio && validatedDimension;
  }

  const handleOnDelete = useCallback(
    (e: MouseEvent<HTMLImageElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (onDelete) {
        onDelete();
      }
    },
    [onDelete],
  );

  function StopPropagation(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  function handleOnPreview() {
    if (localImageUrl) setPhotoSource(localImageUrl);
  }

  function closePhotoModal() {
    setPhotoSource("");
  }

  const uploadButton = useMemo(() => {
    switch (mode) {
      case "small":
        return <Button icon={<UploadOutlined />}>上傳</Button>;
      case "normal":
      default:
        return (
          <UploadButtonWrapper>
            {isLoading ? <LoadingOutlined /> : <PlusOutlined />}
            <UploadButtonText>Upload</UploadButtonText>
          </UploadButtonWrapper>
        );
    }
  }, [mode, isLoading]);

  const imageWrapper = useMemo(() => {
    switch (mode) {
      case "small":
        return (
          <SmallImageWrapper>
            <SmallImage src={localImageUrl} />
            <SmallImageTitle>{imageName}</SmallImageTitle>
            <DeleteIcon onClick={handleOnDelete} />
          </SmallImageWrapper>
        );
      case "normal":
      default:
        return <ImageWrapper src={localImageUrl} />;
    }
  }, [mode, localImageUrl, imageName, handleOnDelete]);

  return (
    <>
      <UploadWrapper
        accept="image/*"
        name="avatar"
        listType={mode === "small" ? "text" : "picture-card"}
        showUploadList={false}
        action={handleUploadImage}
        beforeUpload={handleBeforeUpload}
        editable={editable}
        disabled={!editable}
      >
        {localImageUrl ? imageWrapper : uploadButton}
        {hasPreviewAndDelete && localImageUrl && (
          <HoverMask id="upload-hover-mask" onClick={StopPropagation}>
            <MaskIconContainer>
              <PreviewIcon onClick={handleOnPreview} />
              <DeleteOutlined onClick={handleOnDelete} />
            </MaskIconContainer>
          </HoverMask>
        )}
      </UploadWrapper>
      {photoSource && <PhotoModal source={photoSource} onClose={closePhotoModal} />}
    </>
  );
};

export default UploadImageButton;
